import { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import {
    useDeleteDiscountRule,
    usePracticeOptions,
    useSaveDiscountRule
} from '../../hooks/useBilling';
import { useToast } from '../../providers/ToastProvider';
import { apiErrorMessage } from '../../utils/apiError';
import { fetchDiscountRulePractices } from '../../services/billing';
import { SearchSelect } from './SearchSelect';
import { formatMoneyCents } from '../../utils/money';
import type { ProductDiscountRule, ProductRow } from '../../services/billing';
import { useEscapeKey } from '../../hooks/useEscapeKey';

// Rule editor: config (name, applies-to version, type/value, schedule) plus
// the practice eligibility roster. Rules stack at pricing time, so the
// preview shows this rule's effect on its base price in isolation.
//
// `product: null` is the global-registry create flow: a product picker
// appears at the top (rules live on a product) fed by `products`.

interface DiscountRuleModalProps {
    product: ProductRow | null;
    // Catalog for the product picker; required when product is null.
    products?: ProductRow[];
    rule: ProductDiscountRule | null; // null = create
    onClose: () => void;
}

const INPUT_CLASS =
    'appearance-none block w-full h-9 px-2.5 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-[13px] text-gray-800 focus:outline-none focus:border-gray-800 focus:ring-2 focus:ring-gray-100';

const FIELD_LABEL_CLASS = 'text-[13px] font-semibold text-gray-700';

function toDateInput(iso: string | null): string {
    return iso ? iso.slice(0, 10) : '';
}

function toIsoOrNull(dateInput: string): string | null {
    return dateInput ? new Date(dateInput).toISOString() : null;
}

function applyOnce(baseCents: number, type: 'flat' | 'percentage', value: number): number {
    if (type === 'flat') return Math.max(0, baseCents - Math.round(value * 100));
    return Math.round(baseCents * (1 - value / 100));
}

export function DiscountRuleModal({
    product,
    products = [],
    rule,
    onClose
}: DiscountRuleModalProps) {
    const isEdit = rule !== null;
    const { showToast } = useToast();
    const { practices } = usePracticeOptions();
    const { mutateAsync: saveRule, isPending: isSaving } = useSaveDiscountRule();
    const { mutateAsync: deleteRule, isPending: isDeleting } = useDeleteDiscountRule();

    useEscapeKey(onClose, !isSaving && !isDeleting);

    const [selectedProductId, setSelectedProductId] = useState('');
    // Fixed product (opened from a product / a registry row) or picker choice.
    const activeProduct = product ?? products.find((p) => p.id === selectedProductId) ?? null;

    const [name, setName] = useState(rule?.name ?? '');
    const [appliesTo, setAppliesTo] = useState(rule?.appliesToVersionId ?? '');
    const [discountType, setDiscountType] = useState<'flat' | 'percentage'>(
        rule?.discountType ?? 'percentage'
    );
    const [value, setValue] = useState(rule ? String(rule.discountValue) : '');
    const [startsAt, setStartsAt] = useState(toDateInput(rule?.startsAt ?? null));
    const [endsAt, setEndsAt] = useState(toDateInput(rule?.endsAt ?? null));
    const [eligibleIds, setEligibleIds] = useState<Set<string>>(new Set());
    const [rosterSearch, setRosterSearch] = useState('');

    const productOptions = useMemo(
        () =>
            products.map((p) => ({
                value: p.id,
                label: p.name,
                sublabel: p.category ?? undefined,
                keywords: [p.category, p.subCategory].filter(Boolean) as string[]
            })),
        [products]
    );

    // Load the existing roster once when editing; a dirty flag keeps a slow
    // response from clobbering checkboxes the admin already toggled.
    const rosterTouched = useRef(false);
    const { data: rosterData, isLoading: rosterLoading } = useQuery({
        queryKey: ['v2/products', product?.id, 'discount-rules', rule?.id, 'practices'],
        queryFn: () => fetchDiscountRulePractices(product!.id, rule!.id),
        enabled: isEdit && product !== null,
        staleTime: 0
    });
    useEffect(() => {
        if (rosterData && !rosterTouched.current) setEligibleIds(new Set(rosterData));
    }, [rosterData]);

    const addEligible = (practiceId: string) => {
        rosterTouched.current = true;
        setEligibleIds((prev) => new Set(prev).add(practiceId));
    };

    const removeEligible = (practiceId: string) => {
        rosterTouched.current = true;
        setEligibleIds((prev) => {
            const next = new Set(prev);
            next.delete(practiceId);
            return next;
        });
    };

    const setAll = (ids: string[]) => {
        rosterTouched.current = true;
        setEligibleIds(new Set(ids));
    };

    // The roster shows ONLY eligible practices; the rest live in the add picker.
    const filteredEligible = useMemo(() => {
        const query = rosterSearch.trim().toLowerCase();
        const eligible = practices.filter((p) => eligibleIds.has(p.id));
        if (!query) return eligible;
        return eligible.filter(
            (p) =>
                p.name.toLowerCase().includes(query) ||
                (p.doctorName ?? '').toLowerCase().includes(query)
        );
    }, [practices, eligibleIds, rosterSearch]);

    const addOptions = useMemo(
        () =>
            practices
                .filter((p) => !eligibleIds.has(p.id))
                .map((p) =>
                    p.doctorName
                        ? {
                              value: p.id,
                              label: p.name,
                              sublabel: `Dr. ${p.doctorName}`,
                              keywords: [p.doctorName]
                          }
                        : { value: p.id, label: p.name }
                ),
        [practices, eligibleIds]
    );

    const numericValue = Number(value);
    const valueValid =
        value !== '' &&
        !isNaN(numericValue) &&
        numericValue > 0 &&
        (discountType !== 'percentage' || numericValue <= 100);

    // Preview base: the scoped version's price, or the shared price when every
    // version agrees; a mixed-price product previews nothing.
    const baseCents = useMemo(() => {
        if (!activeProduct) return null;
        if (appliesTo)
            return activeProduct.versions.find((v) => v.id === appliesTo)?.priceCents ?? null;
        const prices = [...new Set(activeProduct.versions.map((v) => v.priceCents))];
        return prices.length === 1 ? prices[0] : null;
    }, [activeProduct, appliesTo]);
    const effectiveCents =
        baseCents !== null && valueValid ? applyOnce(baseCents, discountType, numericValue) : null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!activeProduct) {
            showToast('Pick a product for the rule', 'error');
            return;
        }
        if (!name.trim()) {
            showToast('Rule name is required', 'error');
            return;
        }
        if (!valueValid) {
            showToast(
                discountType === 'percentage'
                    ? 'Enter a percentage between 0 and 100'
                    : 'Enter a valid amount',
                'error'
            );
            return;
        }

        try {
            await saveRule({
                productId: activeProduct.id,
                ruleId: rule?.id,
                body: {
                    name: name.trim(),
                    discountType,
                    discountValue: numericValue,
                    appliesToVersionId: appliesTo || null,
                    startsAt: toIsoOrNull(startsAt),
                    endsAt: toIsoOrNull(endsAt),
                    note: null,
                    practiceIds: [...eligibleIds]
                }
            });
            showToast(isEdit ? 'Rule updated' : 'Rule created', 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to save rule'), 'error');
        }
    };

    const handleDelete = async () => {
        if (!rule || !activeProduct) return;
        try {
            await deleteRule({ productId: activeProduct.id, ruleId: rule.id });
            showToast('Rule deleted', 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to delete rule'), 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[60] flex justify-center items-center p-6">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[720px] max-h-[90vh] flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">
                            {isEdit ? 'Discount rule' : 'New discount rule'}
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {activeProduct
                                ? activeProduct.name
                                : 'Rules live on a product. Pick one to begin.'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <form
                    onSubmit={(e) => {
                        void handleSubmit(e);
                    }}
                    className="flex flex-col min-h-0 flex-1"
                >
                    <div className="p-6 flex flex-col gap-5 overflow-y-auto flex-1">
                        {/* Product picker - registry create flow only. */}
                        {product === null && (
                            <label className="flex flex-col gap-1.5">
                                <span className={FIELD_LABEL_CLASS}>Product *</span>
                                <SearchSelect
                                    value={selectedProductId}
                                    options={productOptions}
                                    onChange={(id) => {
                                        setSelectedProductId(id);
                                        setAppliesTo('');
                                    }}
                                    placeholder="Select a product…"
                                    searchPlaceholder="Search products..."
                                />
                            </label>
                        )}

                        {/* Rule config */}
                        <div className="grid grid-cols-2 gap-4">
                            <label className="flex flex-col gap-1.5 col-span-2">
                                <span className={FIELD_LABEL_CLASS}>Rule name *</span>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    placeholder="e.g. Concierge program rate"
                                    className={INPUT_CLASS}
                                />
                            </label>
                            <label className="flex flex-col gap-1.5">
                                <span className={FIELD_LABEL_CLASS}>Applies to</span>
                                <select
                                    value={appliesTo}
                                    onChange={(e) => setAppliesTo(e.target.value)}
                                    disabled={!activeProduct}
                                    className={`${INPUT_CLASS} disabled:bg-gray-50 disabled:cursor-not-allowed`}
                                >
                                    <option value="">All versions</option>
                                    {(activeProduct?.versions ?? []).map((version) => (
                                        <option key={version.id} value={version.id}>
                                            {version.label} version
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="flex flex-col gap-1.5">
                                    <span className={FIELD_LABEL_CLASS}>Type</span>
                                    <select
                                        value={discountType}
                                        onChange={(e) =>
                                            setDiscountType(e.target.value as 'flat' | 'percentage')
                                        }
                                        className={INPUT_CLASS}
                                    >
                                        <option value="percentage">Percent</option>
                                        <option value="flat">Fixed amount</option>
                                    </select>
                                </label>
                                <label className="flex flex-col gap-1.5">
                                    <span className={FIELD_LABEL_CLASS}>Value *</span>
                                    <div className="relative">
                                        {discountType === 'flat' && (
                                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-gray-400">
                                                $
                                            </span>
                                        )}
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={value}
                                            onChange={(e) => setValue(e.target.value)}
                                            required
                                            className={`${INPUT_CLASS} ${discountType === 'flat' ? 'pl-6' : 'pr-7'}`}
                                        />
                                        {discountType === 'percentage' && (
                                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[13px] text-gray-400">
                                                %
                                            </span>
                                        )}
                                    </div>
                                </label>
                            </div>
                            <label className="flex flex-col gap-1.5">
                                <span className={FIELD_LABEL_CLASS}>Starts</span>
                                <input
                                    type="date"
                                    value={startsAt}
                                    onChange={(e) => setStartsAt(e.target.value)}
                                    className={INPUT_CLASS}
                                />
                                <span className="text-[11px] text-gray-400">
                                    Empty = active immediately.
                                </span>
                            </label>
                            <label className="flex flex-col gap-1.5">
                                <span className={FIELD_LABEL_CLASS}>Ends</span>
                                <input
                                    type="date"
                                    value={endsAt}
                                    onChange={(e) => setEndsAt(e.target.value)}
                                    className={INPUT_CLASS}
                                />
                                <span className="text-[11px] text-gray-400">
                                    Empty = no end date.
                                </span>
                            </label>
                        </div>

                        {/* Effective preview - this rule alone; other rules may stack. */}
                        {effectiveCents !== null && baseCents !== null && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-4">
                                <span className="text-xs text-gray-500">After this rule</span>
                                <span className="text-sm text-gray-400 line-through tabular-nums">
                                    {formatMoneyCents(baseCents)}
                                </span>
                                <span className="text-base font-bold text-gray-900 tabular-nums">
                                    {formatMoneyCents(effectiveCents)}
                                </span>
                                <span className="ml-auto text-xs text-gray-400">
                                    {eligibleIds.size} of {practices.length} practices eligible
                                </span>
                            </div>
                        )}

                        {/* Eligibility roster - eligible practices only; everyone else
                joins through the add picker. */}
                        <div className="flex flex-col gap-2.5">
                            <div className="flex items-center justify-between">
                                <span className={FIELD_LABEL_CLASS}>
                                    Eligible practices
                                    <span className="ml-1.5 text-gray-400 font-normal">
                                        {eligibleIds.size}
                                    </span>
                                </span>
                                <div className="flex items-center gap-2.5 text-[12px]">
                                    <button
                                        type="button"
                                        onClick={() => setAll(practices.map((p) => p.id))}
                                        className="font-medium text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-600"
                                    >
                                        Add all
                                    </button>
                                    <span className="text-gray-200">·</span>
                                    <button
                                        type="button"
                                        onClick={() => setAll([])}
                                        className="font-medium text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-600"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                            {eligibleIds.size > 8 && (
                                <div className="flex items-center h-9 bg-gray-50 border border-gray-200 rounded-lg px-3">
                                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 mr-2 shrink-0" />
                                    <input
                                        type="text"
                                        value={rosterSearch}
                                        onChange={(e) => setRosterSearch(e.target.value)}
                                        placeholder="Filter list…"
                                        className="bg-transparent outline-none text-[13px] text-gray-700 placeholder-gray-400 w-full"
                                    />
                                </div>
                            )}
                            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden max-h-[240px] overflow-y-auto">
                                {rosterLoading ? (
                                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                                        Loading eligibility…
                                    </div>
                                ) : filteredEligible.length === 0 ? (
                                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                                        {eligibleIds.size === 0
                                            ? 'No practices eligible yet - only eligible practices get this rate.'
                                            : 'No eligible practices match the filter.'}
                                    </div>
                                ) : (
                                    filteredEligible.map((practice) => (
                                        <div
                                            key={practice.id}
                                            className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50"
                                        >
                                            <span className="text-sm font-medium text-gray-800 flex-1 truncate">
                                                {practice.name}
                                            </span>
                                            {practice.doctorName && (
                                                <span className="text-xs text-gray-400 shrink-0">
                                                    Dr. {practice.doctorName}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => removeEligible(practice.id)}
                                                className="w-6 h-6 rounded flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 shrink-0"
                                                aria-label={`Remove ${practice.name}`}
                                            >
                                                <XMarkIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                            <SearchSelect
                                value=""
                                options={addOptions}
                                onChange={addEligible}
                                placeholder="+ Add eligible practice…"
                                searchPlaceholder="Search practices..."
                                disabled={addOptions.length === 0}
                            />
                        </div>
                    </div>

                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between shrink-0">
                        {isEdit ? (
                            <button
                                type="button"
                                onClick={() => {
                                    void handleDelete();
                                }}
                                disabled={isDeleting}
                                className="flex items-center gap-2 text-[13px] font-semibold text-red-600 px-3 py-2 rounded-lg hover:bg-red-50 disabled:opacity-50"
                            >
                                <TrashIcon className="h-4 w-4" />
                                Delete rule
                            </button>
                        ) : (
                            <span />
                        )}
                        <div className="flex items-center gap-2.5">
                            <button
                                type="button"
                                onClick={onClose}
                                className="text-sm font-semibold text-gray-500 px-4 py-2 hover:text-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold shadow hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSaving ? 'Saving…' : 'Save rule'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
